import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return { data, error };
  };

  // Default to signup mode if on /signup route
  const [isLogin, setIsLogin] = useState(location.pathname !== '/signup');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    fullName: ''
  });

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');

  // Check for verification token when in signup mode
  useEffect(() => {
    if (!isLogin) {
      const verificationToken = localStorage.getItem('verificationToken');
      if (!verificationToken) {
        // No verification token, redirect to activation code verification
        navigate('/signup/verify-code');
      }
    }
  }, [isLogin, navigate]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error on input change
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Registration validation
    if (!isLogin) {
      // Check emails match
      if (formData.email !== formData.confirmEmail) {
        setError('Email addresses do not match');
        setLoading(false);
        return;
      }

      // Check passwords match
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      // Check password length
      if (formData.password.length < 12) {
        setError('Password must be at least 12 characters long');
        setLoading(false);
        return;
      }
    }

    try {
      if (isLogin) {
        // Login
        const result = await login(formData.email, formData.password);

        // Small delay to ensure localStorage is written
        await new Promise(resolve => setTimeout(resolve, 100));

        // Get user from localStorage to check role
        const userData = JSON.parse(localStorage.getItem('user'));

        // Redirect based on role
        const isAdvisor = userData?.role === 'advisor' || userData?.role?.trim() === 'advisor';

        if (isAdvisor) {
          navigate('/advisor', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        // Register - get verification token from localStorage
        const verificationToken = localStorage.getItem('verificationToken');
        if (!verificationToken) {
          setError('Verification token expired. Please verify your activation code again.');
          navigate('/signup/verify-code');
          return;
        }

        await register(formData.email, formData.password, formData.fullName, verificationToken);

        // Clear verification token after successful registration
        localStorage.removeItem('verificationToken');

        navigate('/dashboard');
      }
    } catch (err) {
      logger.error('Authentication error', { error: err, email: formData.email, isLogin });
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setSocialLoading(true);
    setError('');

    // For signup, check for verification token
    if (!isLogin) {
      const verificationToken = localStorage.getItem('verificationToken');
      if (!verificationToken) {
        setError('Verification token expired. Please verify your activation code again.');
        setSocialLoading(false);
        navigate('/signup/verify-code');
        return;
      }

      // Store verification token for OAuth callback to use
      localStorage.setItem('pendingVerificationToken', verificationToken);
    }

    try {
      await signInWithGoogle();
      // User will be redirected to Google, no need to reset loading state
    } catch (err) {
      logger.error('Google sign-in error', { error: err });
      setError('Failed to sign in with Google. Please try again.');
      setSocialLoading(false);
      // Clear stored verification token on error
      if (!isLogin) {
        localStorage.removeItem('pendingVerificationToken');
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordError('');
    setForgotPasswordMessage('');

    if (!forgotPasswordEmail.trim()) {
      setForgotPasswordError('Email is required');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail })
      });

      const data = await response.json();

      if (response.ok) {
        setForgotPasswordMessage('Password reset link sent! Please check your email.');
        // Auto-close modal after 3 seconds
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotPasswordEmail('');
          setForgotPasswordMessage('');
        }, 3000);
      } else {
        setForgotPasswordError(data.error || 'Failed to send reset email');
      }
    } catch (error) {
      setForgotPasswordError('Network error. Please try again.');
    }
  };

  const closeForgotPasswordModal = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordMessage('');
    setForgotPasswordError('');
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{
        backgroundColor: '#1f2937',
        backgroundImage: 'url(/Background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="flex justify-center mb-8">
        <img
          src="/Login Logo.png"
          alt="Dominion Business Advisors"
          className="h-40 w-auto"
          loading="eager"
          decoding="async"
          style={{ contentVisibility: 'auto' }}
        />
      </div>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Exit Ready Pro</h1>
          <p className="font-boldtext-gray-900 mt-2"> Brought to you by Dominion Business Advisors</p>
          <p className="text-gray-600 mt-2">
            {isLogin ? 'Welcome back!' : 'Create your account'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required={!isLogin}
                  className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="you@example.com"
            />
          </div>

          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Email
              </label>
              <input
                type="email"
                name="confirmEmail"
                value={formData.confirmEmail}
                onChange={handleChange}
                required={!isLogin}
                className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Confirm your email"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={isLogin ? 6 : 12}
              className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="••••••••"
            />
            {!isLogin && (
              <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-700 mb-1">Password Requirements:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li className="flex items-start">
                    <span className="mr-1">•</span>
                    <span>At least 12 characters long</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-1">•</span>
                    <span>Mix of uppercase and lowercase letters recommended</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-1">•</span>
                    <span>Include numbers and special characters for extra security</span>
                  </li>
                </ul>
              </div>
            )}
            {isLogin && (
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-blue-600 hover:text-blue-500 transition-colors"
                >
                  Forgot your password?
                </button>
              </div>
            )}
          </div>

          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required={!isLogin}
                minLength={12}
                className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Confirm your password"
              />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || socialLoading}
            className="w-full bg-blue-600 text-white py-2 px-4 min-h-[44px] text-base rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                Loading...
              </div>
            ) : (
              isLogin ? 'Sign In' : 'Sign Up'
            )}
          </button>
        </form>

        {/* Social Login Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        {/* Social Login Buttons */}
        <div>
          <button
            onClick={handleGoogleSignIn}
            disabled={loading || socialLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-2 min-h-[44px] text-base border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
          >
            {socialLoading ? (
              <div className="animate-spin h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full"></div>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => {
              if (isLogin) {
                // Going to signup - need to verify activation code first
                navigate('/signup/verify-code');
              } else {
                // Going to login
                navigate('/login');
              }
            }}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Reset Password
              </h3>
              <button
                onClick={closeForgotPasswordModal}
                className="text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="block w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <strong>Note:</strong> If you don't see the email in your inbox within a few minutes, please check your spam or junk folder.
                </p>
              </div>

              {forgotPasswordError && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                  {forgotPasswordError}
                </div>
              )}

              {forgotPasswordMessage && (
                <div className="text-sm text-green-600 bg-green-50 p-3 rounded-lg">
                  {forgotPasswordMessage}
                </div>
              )}

              <button
                type="submit"
                className="w-full py-2 px-4 min-h-[44px] text-base bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Send Reset Link
              </button>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default LoginForm;
